import { useAnalytics } from '@ecosystem/newron-client-lib';
import { feedback } from '@ecosystem/newron-design-system';
import { ColumnConfig, SortingOptionsType, Table, TableBody } from '@components/design/migrated/Table';
import { capitalize } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ShareTypeMapping } from '@const/PerformanceType';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import {
  ColumnHeaderDisplayNameForPA,
  ColumnHeaderForPA,
  DefaultSortingOptionsForPerformanceAnalysisboard,
  PerformanceAnalysisboardPg,
  PerformanceAnalysisboardTableId,
} from '@models/PerformanceAnalysisboard';
import { NoResult } from '@pages/NoResult';
import usePersistedSorting from '@utils/hook/sorting/UsePersistedSorting';
import { ChartElementName, ChartElementType } from '@utils/page-analytics/model/ChartInteractionTracking';
import {
  TABLE_CLICKED_TRACKING_TITLE,
  tableClickTrackingFunction,
  TableClickTrackingParam,
} from '@utils/page-analytics/model/TableClickTracking';
import { FactRevenue, Filters, getPageTypeWithPageName, PageName, ShareType } from '@utils/pageConfig/Types';
import * as styles from '@utils/tableViewscss/TableViewBoard.module.scss';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import { getKpiDisplayNameFromHeaderKey } from '@hooks/export/ExportUtils';
import { useInViewTrackingForPerformanceAnalysis } from '../performance-analysis-card/PerformanceAnalysisCardHooks';
import { PerformanceAnalysisView, PerformanceAnalysisViewMapping } from '../performance-analysis-card/Types';
import { getPerformanceAnalysisboardColumns } from './performance-analysis-table-columns/PerformanceAnalysisTableColumns';
import PerformanceAnalysisTableHead from './performance-analysis-table-head/PerformanceAnalysisTableHead';
import PerformanceAnalysisTableRow from './performance-analysis-table-row/PerformanceAnalysisTableRow';
import { formatCurrencyLabelColumnConfigList } from '@utils/currencyUtils/CurrencyUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { PERFORMANCE_ANALYSIS_CARD_TITLE } from '@pages/Utilities';
import ErrorResultMessage from '@pages/ErrorResultMessage';

interface Props {
  performanceAnalysisboardPg: PerformanceAnalysisboardPg[];
  selectedShareType: ShareType;
  selectedGrowthType: GrowthTypes;
  loadMore: boolean;
  isLoading: boolean;
  countries: Filters;
  factRevenue: FactRevenue;
  noResultHandler: () => void;
  volume: string;
  noPeriodExistForMulticountry?: boolean;
  currentTotalName: string;
  isApiError?: boolean;
}

function handleTableClickTracking(
  sideFiltersState: any,
  retailerClientState: any,
  track: (event: string, properties?: Object | undefined) => void,
  options: SortingOptionsType,
  volume: string,
  access: Object,
) {
  const trackingParam: TableClickTrackingParam = {
    eventTitle: TABLE_CLICKED_TRACKING_TITLE,
    sideFilters: sideFiltersState.appliedFilters,
    retailerClientState: retailerClientState,
    track: track,
    pageType: getPageTypeWithPageName(PageName.PERFORMANCE_ANALYSIS),
    pageName: PageName.PERFORMANCE_ANALYSIS,
    componentName: ChartElementName.TABLE_SORT,
    elementType: ChartElementType.SORT,
    optionChosen: `${options.label} ${capitalize(options.sortDir)}`,
    column: getKpiDisplayNameFromHeaderKey(options.headerKey, volume),
    access: access,
  };
  tableClickTrackingFunction(trackingParam);
}

const PerformanceAnalysisboard: React.FC<Props> = ({
  performanceAnalysisboardPg,
  selectedShareType,
  selectedGrowthType,
  loadMore,
  isLoading,
  countries,
  factRevenue,
  noResultHandler,
  volume,
  noPeriodExistForMulticountry,
  currentTotalName,
  isApiError,
}) => {
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const { ref } = useInViewTrackingForPerformanceAnalysis(
    PerformanceAnalysisView.Table,
    ShareTypeMapping[selectedShareType],
    performanceAnalysisboardPg.length > 0,
    `${PERFORMANCE_ANALYSIS_CARD_TITLE} ${PerformanceAnalysisViewMapping[PerformanceAnalysisView.Table]}`,
  );

  const renderTableBody = () => {
    const total = performanceAnalysisboardPg.find((product) => product.segmentOrPg === currentTotalName);

    const productGroups = performanceAnalysisboardPg.filter((product) => product.segmentOrPg !== currentTotalName);

    return (
      <>
        {total && (
          <PerformanceAnalysisTableHead
            performanceAnalysisboardPg={total}
            columns={columns}
            sortedColumn={sorting.headerKey}
            selectedGrowthType={selectedGrowthType}
            selectedShareType={selectedShareType}
            countries={countries}
            currencySymbol={factRevenue.currencySymbol}
          />
        )}
        <TableBody>
          {productGroups.map((productGroup) => (
            <PerformanceAnalysisTableRow
              key={productGroup.segmentOrPg}
              performanceAnalysisboardPg={productGroup}
              columns={columns}
              sortedColumn={sorting.headerKey}
              className={styles.TableViewboard__TableRow__WithHover}
              selectedGrowthType={selectedGrowthType}
              selectedShareType={selectedShareType}
              countries={countries}
              currencySymbol={factRevenue.currencySymbol}
            />
          ))}
        </TableBody>
      </>
    );
  };

  const { sorting, updateOptions } = usePersistedSorting({
    tableId: PerformanceAnalysisboardTableId,
    defaultSorting: DefaultSortingOptionsForPerformanceAnalysisboard,
  });

  // PA table will not have Revenue and Volume Share (Market vs Retailer) options
  const invalidKpiHeaderKeys: string[] = [
    ColumnHeaderForPA.marketRetailerRevenueShare,
    ColumnHeaderForPA.marketRetailerVolumeShare,
  ];

  const updateSort = useCallback(
    (options: SortingOptionsType) => {
      handleTableClickTracking(sideFiltersState, retailerClientState, track, options, volume, pagesConfig.access);

      updateOptions(options);
    },
    [updateOptions],
  );

  useEffect(() => {
    if (performanceAnalysisboardPg) {
      const cols: ColumnConfig[] = [
        {
          headerKey: ColumnHeaderForPA.categoryGroup,
          name: ColumnHeaderDisplayNameForPA()[ColumnHeaderForPA.categoryGroup],
          className: 'z-20',
        },
        ...getPerformanceAnalysisboardColumns(selectedShareType, volume),
      ];
      const validCols = cols.filter((col) => !invalidKpiHeaderKeys.includes(col.headerKey));
      setColumns(formatCurrencyLabelColumnConfigList(validCols, factRevenue));
    }
  }, [performanceAnalysisboardPg, selectedShareType, sorting]);

  useEffect(() => {
    if (performanceAnalysisboardPg.length === 0) {
      noResultHandler();
    }
  }, [performanceAnalysisboardPg]);

  return (
    <>
      {renderVisualization({
        isLoading: isLoading,
        fetchedApiData: performanceAnalysisboardPg,
        defaultData: [],
        jsxToRender: (
          <div data-testid="Performance-Analysisboard" ref={ref}>
            <Table
              dontWrap={true}
              columns={columns}
              isHeaderSticky={true}
              sortingOptions={sorting}
              setSortingOptions={updateSort}
              defaultSortingOptions={sorting}
              className={styles.TableViewboard__Table}
            >
              {renderTableBody()}
              {loadMore && (
                <tbody>
                  <tr>
                    <td colSpan={columns.length}>
                      <feedback.DotsLoader />
                    </td>
                  </tr>
                </tbody>
              )}
            </Table>
          </div>
        ),
        noResultJsx: <div ref={ref}>{NoResult()}</div>,
        errorMessageJsx: <div ref={ref}>{ErrorResultMessage()}</div>,
        noPeriodExistForMulticountry: noPeriodExistForMulticountry,
        isError: isApiError,
      })}
    </>
  );
};

export default PerformanceAnalysisboard;
